import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Shield, Edit2, Save, X, Terminal, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const load = async () => {
    try {
      const res = await adminAPI.agents();
      setAgents(res.data.agents);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (agentId, currentActive) => {
    try {
      await adminAPI.updateAgent(agentId, { is_active: !currentActive });
      toast.success(`Agent ${!currentActive ? 'activated' : 'deactivated'}`);
      load();
    } catch { toast.error('Update failed'); }
  };

  const handleSave = async (agentId) => {
    try {
      const update = {};
      if (editData.price_per_run !== undefined) update.price_per_run = parseFloat(editData.price_per_run);
      if (editData.system_prompt !== undefined) update.system_prompt = editData.system_prompt;
      await adminAPI.updateAgent(agentId, update);
      toast.success('Agent updated');
      setEditing(null);
      load();
    } catch { toast.error('Update failed'); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-agents-page">
        <div className="mb-8">
          <p className="font-mono text-xs text-[#ff003c] tracking-widest uppercase mb-2">AGENT MANAGEMENT</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Agents ({agents.length})</h1>
          <p className="font-body text-sm text-[#999] mt-1">Toggle agents on/off, edit pricing, and update system prompts</p>
        </div>

        <div className="space-y-3">
          {agents.map(agent => (
            <div key={agent.agent_id} className="card-cyber rounded-sm overflow-hidden" data-testid={`admin-agent-${agent.agent_id}`}>
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-white/[0.03] border border-white/5 flex-shrink-0">
                      <Shield className="w-5 h-5 text-[#FF7A3D]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h3 className="font-heading text-sm font-bold text-white">{agent.name}</h3>
                        <Badge className={`font-mono text-[10px] uppercase ${agent.tier === 'flagship' ? 'bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20' : agent.tier === 'premium' ? 'bg-[#ffbd2e]/10 text-[#ffbd2e] border-[#ffbd2e]/20' : 'bg-[#00E5C1]/10 text-[#00E5C1] border-[#00E5C1]/20'}`}>
                          {agent.tier}
                        </Badge>
                      </div>
                      <p className="font-mono text-[10px] text-[#aaa]">{agent.agent_id}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right mr-4">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-mono text-[10px] text-[#aaa]">RUNS</p>
                          <p className="font-mono text-xs text-[#00E5C1]">{agent.exec_count || 0}</p>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] text-[#aaa]">EARNED</p>
                          <p className="font-mono text-xs text-[#FF7A3D]">{(agent.total_credits_earned || 0).toFixed(2)} cr</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-[#aaa]">{agent.is_active ? 'ON' : 'OFF'}</span>
                      <Switch
                        checked={agent.is_active}
                        onCheckedChange={() => handleToggle(agent.agent_id, agent.is_active)}
                        data-testid={`toggle-${agent.agent_id}`}
                      />
                    </div>
                    <button onClick={() => { setEditing(editing === agent.agent_id ? null : agent.agent_id); setEditData({ price_per_run: agent.price_per_run, system_prompt: agent.system_prompt || '' }); }} className="text-[#aaa] hover:text-[#FF7A3D]" data-testid={`edit-agent-${agent.agent_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {editing === agent.agent_id && (
                  <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[10px] text-[#aaa] tracking-widest block mb-1">PRICE PER RUN (CREDITS)</label>
                        <Input
                          type="number" step="0.01"
                          value={editData.price_per_run}
                          onChange={(e) => setEditData({...editData, price_per_run: e.target.value})}
                          className="bg-black border-white/10 font-mono text-xs"
                          data-testid={`price-input-${agent.agent_id}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] text-[#aaa] tracking-widest block mb-1">SYSTEM PROMPT</label>
                      <textarea
                        value={editData.system_prompt}
                        onChange={(e) => setEditData({...editData, system_prompt: e.target.value})}
                        rows={4}
                        className="w-full bg-black border border-white/10 rounded-sm p-3 font-mono text-xs text-white resize-y focus:border-[#FF7A3D] focus:outline-none"
                        placeholder="Enter system prompt..."
                        data-testid={`prompt-input-${agent.agent_id}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSave(agent.agent_id)} className="bg-[#FF7A3D] text-black font-heading text-[10px] uppercase rounded-sm h-8 px-4" data-testid={`save-agent-${agent.agent_id}`}>
                        <Save className="w-3 h-3 mr-1" /> SAVE
                      </Button>
                      <Button onClick={() => setEditing(null)} variant="outline" className="border-white/10 text-[#888] font-heading text-[10px] uppercase rounded-sm h-8 px-4 bg-transparent">
                        <X className="w-3 h-3 mr-1" /> CANCEL
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
